'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function TaxCalculator() {
  const t = useTranslations("tools.taxCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const [inputs, setInputs] = useState({
    filingStatus: 'single',
    taxYear: '2025',
    youngDependents: 0,
    otherDependents: 0,
    wages: 80000,
    interest: 0,
    dividends: 0,
    qualifiedDividends: 0,
    capitalGains: 0,
    otherIncome: 0,
    deductionType: 'standard',
    mortgageInterest: 0,
    propertyTax: 0,
    charitable: 0,
    medical: 0,
    studentLoan: 0,
    ira: 0,
    childcare: 0,
    withheld: 9000,
    estimated: 0,
  });

  const [results, setResults] = useState<any>(null);

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2, margin: 0 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6, margin: '1rem 0 0 0' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        borderRadius: '1rem',
        marginBottom: '2rem',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { maxWidth: '100%', margin: '0 auto', background: 'white', borderRadius: '1rem', overflow: 'hidden' },
      {},
      { maxWidth: '700px' },
      { maxWidth: '900px' }
    ),
    toolHeader: {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      padding: '2rem',
      textAlign: 'center' as const,
    },
    toolHeaderTitle: getResponsiveStyle(
      { margin: 0, fontWeight: 700, color: 'white' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    calculatorForm: getResponsiveStyle(
      {},
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    sectionTitle: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, margin: '2rem 0 1rem 0', paddingBottom: '0.5rem', borderBottom: '3px solid #2563eb' },
      { fontSize: '1.25rem' },
      { fontSize: '1.35rem' },
      { fontSize: '1.5rem' }
    ),
    sectionTitleFirst: {
      marginTop: 0,
    },
    formRow: getResponsiveStyle(
      { display: 'grid', gap: '1.5rem', marginBottom: '1.5rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr' },
      { gridTemplateColumns: '1fr 1fr' }
    ),
    formGroup: {
      marginBottom: '1.5rem',
    },
    label: getResponsiveStyle(
      { display: 'block', fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    input: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const,
      },
      { padding: '0.75rem', fontSize: '1rem' },
      { padding: '0.875rem', fontSize: '1.05rem' },
      { padding: '1rem', fontSize: '1.1rem' }
    ),
    select: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const,
      },
      { padding: '0.75rem', fontSize: '1rem' },
      { padding: '0.875rem', fontSize: '1.05rem' },
      { padding: '1rem', fontSize: '1.1rem' }
    ),
    small: {
      display: 'block',
      color: '#64748b',
      fontSize: '0.85rem',
      marginTop: '0.25rem',
    },
    subsection: {
      background: '#f8fafc',
      padding: '1.5rem',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      borderLeft: '4px solid #7c3aed',
    },
    subsectionTitle: getResponsiveStyle(
      { fontWeight: 700, color: '#7c3aed', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.05rem' },
      { fontSize: '1.1rem' }
    ),
    radioGroup: {
      display: 'flex',
      gap: '1.5rem',
      marginTop: '0.5rem',
      flexWrap: 'wrap' as const,
    },
    radioLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      cursor: 'pointer',
      fontWeight: 500,
    },
    calculateBtn: getResponsiveStyle(
      {
        width: '100%',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '0.75rem',
        fontWeight: 700,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        marginTop: '1.5rem',
      },
      { padding: '1rem 2rem', fontSize: '1rem' },
      { padding: '1.1rem 2.2rem', fontSize: '1.1rem' },
      { padding: '1.25rem 2.5rem', fontSize: '1.2rem' }
    ),
    results: getResponsiveStyle(
      { background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderTop: '4px solid #10b981' },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    resultsTitle: getResponsiveStyle(
      { color: '#065f46', margin: '0 0 1.5rem 0', textAlign: 'center' as const },
      { fontSize: '1.35rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' }
    ),
    resultGrid: getResponsiveStyle(
      { display: 'grid', gap: '1rem', marginBottom: '1.5rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(2, 1fr)' },
      { gridTemplateColumns: 'repeat(3, 1fr)' }
    ),
    resultCard: {
      background: 'white',
      padding: '1.25rem',
      borderRadius: '0.75rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
    resultLabel: {
      display: 'block',
      fontSize: '0.9rem',
      color: '#64748b',
      marginBottom: '0.5rem',
      fontWeight: 600,
    },
    resultValue: getResponsiveStyle(
      { display: 'block', fontWeight: 700, color: colors.textPrimary },
      { fontSize: '1.5rem' },
      { fontSize: '1.65rem' },
      { fontSize: '1.75rem' }
    ),
    resultValueHighlight: getResponsiveStyle(
      { display: 'block', fontWeight: 700, color: '#2563eb' },
      { fontSize: '1.65rem' },
      { fontSize: '1.85rem' },
      { fontSize: '2rem' }
    ),
    resultValuePositive: {
      color: '#10b981',
    },
    resultValueNegative: {
      color: '#ef4444',
    },
    breakdown: {
      background: 'white',
      padding: '1.5rem',
      borderRadius: '0.75rem',
      marginTop: '1.5rem',
    },
    breakdownTitle: getResponsiveStyle(
      { color: colors.textPrimary, margin: '0 0 1rem 0' },
      { fontSize: '1rem' },
      { fontSize: '1.05rem' },
      { fontSize: '1.1rem' }
    ),
    breakdownItem: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '0.75rem 0',
      borderBottom: '1px solid #e2e8f0',
    },
    breakdownItemLast: {
      borderBottom: 'none',
      fontWeight: 700,
      paddingTop: '1rem',
      borderTop: '2px solid #2563eb',
    },
    breakdownLabel: {
      color: '#475569',
    },
    breakdownValue: {
      color: colors.textPrimary,
      fontWeight: 600,
    },
    glassCard: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 200, 180, 0.2)',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', marginTop: 0 },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.05rem' },
      { fontSize: '1.1rem' }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.05rem' },
      { fontSize: '1.1rem' }
    ),
    listItem: {
      marginBottom: '0.75rem',
    },
    strong: {
      fontWeight: 700,
      color: colors.textPrimary,
    },
  };

  const taxBrackets2025: any = {
    single: [
      { limit: 11925, rate: 0.10 },
      { limit: 48475, rate: 0.12 },
      { limit: 103350, rate: 0.22 },
      { limit: 197300, rate: 0.24 },
      { limit: 250525, rate: 0.32 },
      { limit: 626350, rate: 0.35 },
      { limit: Infinity, rate: 0.37 }
    ],
    'married-joint': [
      { limit: 23850, rate: 0.10 },
      { limit: 96950, rate: 0.12 },
      { limit: 206700, rate: 0.22 },
      { limit: 394600, rate: 0.24 },
      { limit: 501050, rate: 0.32 },
      { limit: 751600, rate: 0.35 },
      { limit: Infinity, rate: 0.37 }
    ],
    'married-separate': [
      { limit: 11925, rate: 0.10 },
      { limit: 48475, rate: 0.12 },
      { limit: 103350, rate: 0.22 },
      { limit: 197300, rate: 0.24 },
      { limit: 250525, rate: 0.32 },
      { limit: 375800, rate: 0.35 },
      { limit: Infinity, rate: 0.37 }
    ],
    head: [
      { limit: 17000, rate: 0.10 },
      { limit: 64850, rate: 0.12 },
      { limit: 103350, rate: 0.22 },
      { limit: 197300, rate: 0.24 },
      { limit: 250500, rate: 0.32 },
      { limit: 626350, rate: 0.35 },
      { limit: Infinity, rate: 0.37 }
    ]
  };

  const standardDeductions2025: any = {
    single: 15000,
    'married-joint': 30000,
    'married-separate': 15000,
    head: 22500
  };

  const capitalGainsBrackets: any = {
    single: [
      { limit: 48350, rate: 0 },
      { limit: 533400, rate: 0.15 },
      { limit: Infinity, rate: 0.20 }
    ],
    'married-joint': [
      { limit: 96700, rate: 0 },
      { limit: 600050, rate: 0.15 },
      { limit: Infinity, rate: 0.20 }
    ],
    'married-separate': [
      { limit: 48350, rate: 0 },
      { limit: 300025, rate: 0.15 },
      { limit: Infinity, rate: 0.20 }
    ],
    head: [
      { limit: 64750, rate: 0 },
      { limit: 566700, rate: 0.15 },
      { limit: Infinity, rate: 0.20 }
    ]
  };

  const calculateTax = (income: number, brackets: any[]) => {
    let tax = 0;
    let previousLimit = 0;

    for (let bracket of brackets) {
      if (income > bracket.limit) {
        tax += (bracket.limit - previousLimit) * bracket.rate;
        previousLimit = bracket.limit;
      } else {
        tax += (income - previousLimit) * bracket.rate;
        break;
      }
    }

    return tax;
  };

  const calculateCapitalGainsTax = (gains: number, brackets: any[]) => {
    let tax = 0;
    let previousLimit = 0;

    for (let bracket of brackets) {
      if (gains > bracket.limit) {
        tax += (bracket.limit - previousLimit) * bracket.rate;
        previousLimit = bracket.limit;
      } else {
        tax += (gains - previousLimit) * bracket.rate;
        break;
      }
    }

    return tax;
  };

  const formatCurrency = (amount: number) => {
    return '$' + Math.abs(amount).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const grossIncome = inputs.wages + inputs.interest + inputs.dividends + inputs.capitalGains + inputs.otherIncome;

    const studentLoanDeduction = Math.min(inputs.studentLoan, 2500);
    const adjustments = studentLoanDeduction + inputs.ira;
    const agi = Math.max(0, grossIncome - adjustments);

    let deduction = standardDeductions2025[inputs.filingStatus];

    if (inputs.deductionType === 'itemized') {
      const propertyTaxCapped = Math.min(inputs.propertyTax, 10000);
      const medicalDeduction = Math.max(0, inputs.medical - (agi * 0.075));
      const itemizedTotal = inputs.mortgageInterest + propertyTaxCapped + inputs.charitable + medicalDeduction;
      deduction = Math.max(deduction, itemizedTotal);
    }

    const ordinaryIncome = inputs.wages + inputs.interest + (inputs.dividends - inputs.qualifiedDividends) + inputs.otherIncome;
    const taxableOrdinaryIncome = Math.max(0, ordinaryIncome - adjustments - deduction);

    const brackets = taxBrackets2025[inputs.filingStatus];
    const ordinaryTax = calculateTax(taxableOrdinaryIncome, brackets);

    const cgBrackets = capitalGainsBrackets[inputs.filingStatus];
    const qualifiedIncome = inputs.qualifiedDividends + inputs.capitalGains;
    const capitalGainsTax = calculateCapitalGainsTax(qualifiedIncome, cgBrackets);

    const taxBeforeCredits = ordinaryTax + capitalGainsTax;

    const childTaxCredit = inputs.youngDependents * 2000;
    const otherDependentCredit = inputs.otherDependents * 500;
    const childcareCredit = Math.min(inputs.childcare * 0.35, inputs.youngDependents > 0 ? Math.min(6000, inputs.childcare) * 0.35 : 0);

    const totalCredits = childTaxCredit + otherDependentCredit + childcareCredit;

    const totalTax = Math.max(0, taxBeforeCredits - totalCredits);

    const totalPayments = inputs.withheld + inputs.estimated;
    const refundOrOwed = totalPayments - totalTax;

    const effectiveRate = grossIncome > 0 ? (totalTax / grossIncome * 100) : 0;

    setResults({
      totalIncome: grossIncome,
      agi,
      taxableIncome: taxableOrdinaryIncome + qualifiedIncome,
      totalTax,
      effectiveRate,
      refundOrOwed,
      grossIncome,
      adjustments,
      deduction,
      taxBeforeCredits,
      totalCredits,
    });
  };

  const handleInputChange = (field: string, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t("title")}</h1>
        <p style={styles.summaryText}>{t("subtitle")}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.toolHeaderTitle}>{t("toolTitle")}</h2>
          </div>

          <form style={styles.calculatorForm} onSubmit={handleCalculate}>
            <div style={{ ...styles.sectionTitle, ...styles.sectionTitleFirst }}>{t("basicInfoTitle")}</div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="filing-status">{t("filingStatusLabel")}</label>
                <select
                  style={styles.select}
                  id="filing-status"
                  value={inputs.filingStatus}
                  onChange={(e) => handleInputChange('filingStatus', e.target.value)}
                  required
                >
                  <option value="single">{t("filingStatusSingle")}</option>
                  <option value="married-joint">{t("filingStatusMarriedJoint")}</option>
                  <option value="married-separate">{t("filingStatusMarriedSeparate")}</option>
                  <option value="head">{t("filingStatusHead")}</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="tax-year">{t("taxYearLabel")}</label>
                <select
                  style={styles.select}
                  id="tax-year"
                  value={inputs.taxYear}
                  onChange={(e) => handleInputChange('taxYear', e.target.value)}
                  required
                >
                  <option value="2025">{t("taxYear2025")}</option>
                  <option value="2024">{t("taxYear2024")}</option>
                </select>
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="young-dependents">{t("youngDependentsLabel")}</label>
                <input
                  style={styles.input}
                  type="number"
                  id="young-dependents"
                  min="0"
                  value={inputs.youngDependents}
                  onChange={(e) => handleInputChange('youngDependents', parseInt(e.target.value) || 0)}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="other-dependents">{t("otherDependentsLabel")}</label>
                <input
                  style={styles.input}
                  type="number"
                  id="other-dependents"
                  min="0"
                  value={inputs.otherDependents}
                  onChange={(e) => handleInputChange('otherDependents', parseInt(e.target.value) || 0)}
                  required
                />
              </div>
            </div>

            <div style={styles.sectionTitle}>{t("incomeTitle")}</div>

            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="wages">{t("wagesLabel")}</label>
              <input
                style={styles.input}
                type="number"
                id="wages"
                min="0"
                step="0.01"
                value={inputs.wages}
                onChange={(e) => handleInputChange('wages', parseFloat(e.target.value) || 0)}
                required
              />
              <small style={styles.small}>{t("wagesHint")}</small>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="interest">{t("interestLabel")}</label>
              <input
                style={styles.input}
                type="number"
                id="interest"
                min="0"
                step="0.01"
                value={inputs.interest}
                onChange={(e) => handleInputChange('interest', parseFloat(e.target.value) || 0)}
              />
              <small style={styles.small}>{t("interestHint")}</small>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="dividends">{t("dividendsLabel")}</label>
              <input
                style={styles.input}
                type="number"
                id="dividends"
                min="0"
                step="0.01"
                value={inputs.dividends}
                onChange={(e) => handleInputChange('dividends', parseFloat(e.target.value) || 0)}
              />
              <small style={styles.small}>{t("dividendsHint")}</small>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="qualified-dividends">{t("qualifiedDividendsLabel")}</label>
              <input
                style={styles.input}
                type="number"
                id="qualified-dividends"
                min="0"
                step="0.01"
                value={inputs.qualifiedDividends}
                onChange={(e) => handleInputChange('qualifiedDividends', parseFloat(e.target.value) || 0)}
              />
              <small style={styles.small}>{t("qualifiedDividendsHint")}</small>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="capital-gains">{t("capitalGainsLabel")}</label>
              <input
                style={styles.input}
                type="number"
                id="capital-gains"
                min="0"
                step="0.01"
                value={inputs.capitalGains}
                onChange={(e) => handleInputChange('capitalGains', parseFloat(e.target.value) || 0)}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="other-income">{t("otherIncomeLabel")}</label>
              <input
                style={styles.input}
                type="number"
                id="other-income"
                min="0"
                step="0.01"
                value={inputs.otherIncome}
                onChange={(e) => handleInputChange('otherIncome', parseFloat(e.target.value) || 0)}
              />
              <small style={styles.small}>{t("otherIncomeHint")}</small>
            </div>

            <div style={styles.sectionTitle}>{t("deductionsTitle")}</div>

            <div style={styles.formGroup}>
              <label style={styles.label}>{t("deductionTypeLabel")}</label>
              <div style={styles.radioGroup}>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="deduction-type"
                    value="standard"
                    checked={inputs.deductionType === 'standard'}
                    onChange={(e) => handleInputChange('deductionType', e.target.value)}
                  />
                  {t("deductionTypeStandard")}
                </label>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="deduction-type"
                    value="itemized"
                    checked={inputs.deductionType === 'itemized'}
                    onChange={(e) => handleInputChange('deductionType', e.target.value)}
                  />
                  {t("deductionTypeItemized")}
                </label>
              </div>
            </div>

            {inputs.deductionType === 'itemized' && (
              <div style={styles.subsection}>
                <div style={styles.subsectionTitle}>{t("itemizedDeductionsTitle")}</div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="mortgage-interest">{t("mortgageInterestLabel")}</label>
                  <input
                    style={styles.input}
                    type="number"
                    id="mortgage-interest"
                    min="0"
                    step="0.01"
                    value={inputs.mortgageInterest}
                    onChange={(e) => handleInputChange('mortgageInterest', parseFloat(e.target.value) || 0)}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="property-tax">{t("propertyTaxLabel")}</label>
                  <input
                    style={styles.input}
                    type="number"
                    id="property-tax"
                    min="0"
                    step="0.01"
                    value={inputs.propertyTax}
                    onChange={(e) => handleInputChange('propertyTax', parseFloat(e.target.value) || 0)}
                  />
                  <small style={styles.small}>{t("propertyTaxHint")}</small>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="charitable">{t("charitableLabel")}</label>
                  <input
                    style={styles.input}
                    type="number"
                    id="charitable"
                    min="0"
                    step="0.01"
                    value={inputs.charitable}
                    onChange={(e) => handleInputChange('charitable', parseFloat(e.target.value) || 0)}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="medical">{t("medicalLabel")}</label>
                  <input
                    style={styles.input}
                    type="number"
                    id="medical"
                    min="0"
                    step="0.01"
                    value={inputs.medical}
                    onChange={(e) => handleInputChange('medical', parseFloat(e.target.value) || 0)}
                  />
                  <small style={styles.small}>{t("medicalHint")}</small>
                </div>
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="student-loan">{t("studentLoanLabel")}</label>
              <input
                style={styles.input}
                type="number"
                id="student-loan"
                min="0"
                step="0.01"
                value={inputs.studentLoan}
                onChange={(e) => handleInputChange('studentLoan', parseFloat(e.target.value) || 0)}
              />
              <small style={styles.small}>{t("studentLoanHint")}</small>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="ira">{t("iraLabel")}</label>
              <input
                style={styles.input}
                type="number"
                id="ira"
                min="0"
                step="0.01"
                value={inputs.ira}
                onChange={(e) => handleInputChange('ira', parseFloat(e.target.value) || 0)}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="childcare">{t("childcareLabel")}</label>
              <input
                style={styles.input}
                type="number"
                id="childcare"
                min="0"
                step="0.01"
                value={inputs.childcare}
                onChange={(e) => handleInputChange('childcare', parseFloat(e.target.value) || 0)}
              />
              <small style={styles.small}>{t("childcareHint")}</small>
            </div>

            <div style={styles.sectionTitle}>{t("taxPaymentsTitle")}</div>

            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="withheld">{t("withheldLabel")}</label>
              <input
                style={styles.input}
                type="number"
                id="withheld"
                min="0"
                step="0.01"
                value={inputs.withheld}
                onChange={(e) => handleInputChange('withheld', parseFloat(e.target.value) || 0)}
                required
              />
              <small style={styles.small}>{t("withheldHint")}</small>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="estimated">{t("estimatedLabel")}</label>
              <input
                style={styles.input}
                type="number"
                id="estimated"
                min="0"
                step="0.01"
                value={inputs.estimated}
                onChange={(e) => handleInputChange('estimated', parseFloat(e.target.value) || 0)}
              />
            </div>

            <button type="submit" style={styles.calculateBtn}>{t("calculateButton")}</button>
          </form>

          {results && (
            <div style={styles.results}>
              <h3 style={styles.resultsTitle}>{t("resultsTitle")}</h3>

              <div style={styles.resultGrid}>
                <div style={styles.resultCard}>
                  <span style={styles.resultLabel}>{t("totalIncomeLabel")}</span>
                  <span style={styles.resultValue}>{formatCurrency(results.totalIncome)}</span>
                </div>

                <div style={styles.resultCard}>
                  <span style={styles.resultLabel}>{t("agiLabel")}</span>
                  <span style={styles.resultValue}>{formatCurrency(results.agi)}</span>
                </div>

                <div style={styles.resultCard}>
                  <span style={styles.resultLabel}>{t("taxableIncomeLabel")}</span>
                  <span style={styles.resultValue}>{formatCurrency(results.taxableIncome)}</span>
                </div>

                <div style={styles.resultCard}>
                  <span style={styles.resultLabel}>{t("totalTaxLabel")}</span>
                  <span style={styles.resultValueHighlight}>{formatCurrency(results.totalTax)}</span>
                </div>

                <div style={styles.resultCard}>
                  <span style={styles.resultLabel}>{t("effectiveRateLabel")}</span>
                  <span style={styles.resultValue}>{results.effectiveRate.toFixed(2)}%</span>
                </div>

                <div style={styles.resultCard}>
                  <span style={styles.resultLabel}>
                    {results.refundOrOwed >= 0 ? t("refundLabel") : t("owedLabel")}
                  </span>
                  <span style={{
                    ...styles.resultValue,
                    ...(results.refundOrOwed >= 0 ? styles.resultValuePositive : styles.resultValueNegative)
                  }}>
                    {formatCurrency(results.refundOrOwed)}
                  </span>
                </div>
              </div>

              <div style={styles.breakdown}>
                <h4 style={styles.breakdownTitle}>{t("breakdownTitle")}</h4>
                <div style={styles.breakdownItem}>
                  <span style={styles.breakdownLabel}>{t("breakdownGrossIncome")}</span>
                  <span style={styles.breakdownValue}>{formatCurrency(results.grossIncome)}</span>
                </div>
                <div style={styles.breakdownItem}>
                  <span style={styles.breakdownLabel}>{t("breakdownAdjustments")}</span>
                  <span style={styles.breakdownValue}>-{formatCurrency(results.adjustments)}</span>
                </div>
                <div style={styles.breakdownItem}>
                  <span style={styles.breakdownLabel}>{t("breakdownAgi")}</span>
                  <span style={styles.breakdownValue}>{formatCurrency(results.agi)}</span>
                </div>
                <div style={styles.breakdownItem}>
                  <span style={styles.breakdownLabel}>{t("breakdownDeduction")}</span>
                  <span style={styles.breakdownValue}>-{formatCurrency(results.deduction)}</span>
                </div>
                <div style={styles.breakdownItem}>
                  <span style={styles.breakdownLabel}>{t("breakdownTaxableIncome")}</span>
                  <span style={styles.breakdownValue}>{formatCurrency(results.taxableIncome)}</span>
                </div>
                <div style={styles.breakdownItem}>
                  <span style={styles.breakdownLabel}>{t("breakdownTaxBefore")}</span>
                  <span style={styles.breakdownValue}>{formatCurrency(results.taxBeforeCredits)}</span>
                </div>
                <div style={styles.breakdownItem}>
                  <span style={styles.breakdownLabel}>{t("breakdownCredits")}</span>
                  <span style={styles.breakdownValue}>-{formatCurrency(results.totalCredits)}</span>
                </div>
                <div style={{ ...styles.breakdownItem, ...styles.breakdownItemLast }}>
                  <span style={styles.breakdownLabel}>{t("breakdownFinalTax")}</span>
                  <span style={styles.breakdownValue}>{formatCurrency(results.totalTax)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("introTitle")}</h2>
        <p style={styles.paragraph}>{t("introPara1")}</p>
        <p style={styles.paragraph}>{t("introPara2")}</p>
        <p style={styles.paragraph}>{t("introPara3")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("howToUseTitle")}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}><span style={styles.strong}>{t("step1Title")}</span> {t("step1Text")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("step2Title")}</span> {t("step2Text")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("step3Title")}</span> {t("step3Text")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("step4Title")}</span> {t("step4Text")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("step5Title")}</span> {t("step5Text")}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("insightsTitle")}</h2>
        <p style={styles.paragraph}>{t("insightsPara1")}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}><span style={styles.strong}>{t("insight1Title")}</span> {t("insight1Text")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("insight2Title")}</span> {t("insight2Text")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("insight3Title")}</span> {t("insight3Text")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("insight4Title")}</span> {t("insight4Text")}</li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("detailsTitle")}</h2>
        <h3 style={{ ...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem' }}>{t("bracketsTitle")}</h3>
        <p style={styles.paragraph}>{t("bracketsPara")}</p>

        <h3 style={{ ...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem' }}>{t("deductionsDetailsTitle")}</h3>
        <p style={styles.paragraph}>{t("deductionsDetailsPara")}</p>

        <h3 style={{ ...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem' }}>{t("creditsDetailsTitle")}</h3>
        <p style={styles.paragraph}>{t("creditsDetailsPara")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("faqTitle")}</h2>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...styles.h2, fontSize: screenSize === 'mobile' ? '1.1rem' : screenSize === 'tablet' ? '1.2rem' : '1.3rem' }}>{t("faq1Question")}</h3>
          <p style={styles.paragraph}>{t("faq1Answer")}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...styles.h2, fontSize: screenSize === 'mobile' ? '1.1rem' : screenSize === 'tablet' ? '1.2rem' : '1.3rem' }}>{t("faq2Question")}</h3>
          <p style={styles.paragraph}>{t("faq2Answer")}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...styles.h2, fontSize: screenSize === 'mobile' ? '1.1rem' : screenSize === 'tablet' ? '1.2rem' : '1.3rem' }}>{t("faq3Question")}</h3>
          <p style={styles.paragraph}>{t("faq3Answer")}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...styles.h2, fontSize: screenSize === 'mobile' ? '1.1rem' : screenSize === 'tablet' ? '1.2rem' : '1.3rem' }}>{t("faq4Question")}</h3>
          <p style={styles.paragraph}>{t("faq4Answer")}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("referencesTitle")}</h2>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("reference1")}</li>
          <li style={styles.listItem}>{t("reference2")}</li>
          <li style={styles.listItem}>{t("reference3")}</li>
          <li style={styles.listItem}>{t("reference4")}</li>
        </ul>
      </section>
    </div>
  );
}